/* NewUserOnboarding — the one-time guided flow after first sign-in.
   welcome → language → (owner | chatter) → done.  Route: /onboarding
   The fork follows the Signup role chosen at signup. Users created before signup asked
   for it get the role step instead; that choice is session-only and persists nothing. */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import type { SupportedLanguage } from '../../i18n';
import { OnboardingStage } from './OnboardingStage';
import { Panel, Head, Title, Btn, Option, Icon, C, Pose } from './onboardingKit';
import { Lockup } from '../../brand/Brand';

type Step = 'welcome' | 'language' | 'role' | 'owner' | 'chatter' | 'done';
type Role = 'owner' | 'chatter';

const POSE_FOR: Record<Step, Pose> = {
  welcome: 'wave', language: 'explain', role: 'point', owner: 'explain', chatter: 'think', done: 'okay',
};
const DOT_INDEX: Partial<Record<Step, number>> = { language: 0, role: 1, owner: 2, chatter: 2 };
const LANGS: { code: SupportedLanguage; label: string; tag: string }[] = [
  { code: 'en', label: 'English', tag: 'EN' },
  { code: 'uk', label: 'Українська', tag: 'UA' },
  { code: 'ru', label: 'Русский', tag: 'RU' },
  { code: 'es', label: 'Español', tag: 'ES' },
];

export function NewUserOnboarding() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { user, language, setLanguage, completeOnboarding } = useAuth();

  const [step, setStep] = useState<Step>('welcome');
  // Known from signup => the role step is skipped.
  const signupRole = user?.signup_role ?? null;
  const [role, setRole] = useState<Role | null>(signupRole);
  const [hist, setHist] = useState<Step[]>([]);
  const [animKey, setAnimKey] = useState(0);
  const [copied, setCopied] = useState(false);

  const go = (s: Step) => { setHist((h) => [...h, step]); setStep(s); setAnimKey((k) => k + 1); setCopied(false); };
  const back = () => setHist((h) => {
    const prev = h[h.length - 1];
    if (!prev) return h;
    setStep(prev); setAnimKey((k) => k + 1);
    return h.slice(0, -1);
  });

  const dotN = signupRole ? 2 : 3;
  const dotIndex = (DOT_INDEX[step] ?? 0) - (signupRole && (step === 'owner' || step === 'chatter') ? 1 : 0);
  const stepLabel = t('common.step', { i: dotIndex + 1, n: dotN });

  const finish = async () => { await completeOnboarding(); navigate('/dashboard', { replace: true }); };
  const handleClose = async () => { await completeOnboarding(); navigate('/dashboard', { replace: true }); };

  const handleAddAccount = async () => {
    // Owner picked "add account": onboarding is done; hand off to the add-account flow.
    // Sofi just introduced herself, so skip the intro greeting there.
    await completeOnboarding();
    navigate('/accounts/new?from=onboarding', { replace: true });
  };

  const usernameDisplay = user?.username ? `@${user.username}` : (user?.email ?? '');
  const copyUsername = () => {
    try { navigator.clipboard?.writeText(usernameDisplay); } catch { /* clipboard may be blocked */ }
    setCopied(true);
  };

  let content: React.ReactNode = null;

  if (step === 'welcome') content = (
    <Panel anim={animKey} width={540}>
      <Lockup height={13} />
      <h1 style={{ margin: '20px 0 0', color: C.text, fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em' }}>{t('welcome.hi')}</h1>
      <p style={{ color: C.muted, fontSize: 16, lineHeight: 1.6, margin: '13px 0 0' }}>{t('welcome.body')}</p>
      <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
        <Btn onClick={() => go('language')} icon="arrow">{t('welcome.cta')}</Btn>
        <Btn kind="ghost" onClick={finish}>{t('welcome.skip')}</Btn>
      </div>
    </Panel>
  );
  else if (step === 'language') content = (
    <Panel anim={animKey} width={540}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title h={t('language.title')} sub={t('language.sub')} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {LANGS.map((l) => (
          <Option key={l.code} layout="list" selected={language === l.code} onClick={() => { void setLanguage(l.code); }} title={l.label}
            icon={<span style={{ fontWeight: 800, fontSize: 13 }}>{l.tag}</span>} />
        ))}
      </div>
      <div style={{ marginTop: 26 }}><Btn wide onClick={() => go(signupRole ?? 'role')} icon="arrow">{t('language.continue')}</Btn></div>
    </Panel>
  );
  else if (step === 'role') content = (
    <Panel anim={animKey} width={620}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title h={t('role.title')} sub={t('role.sub')} />
      <div className="mobile-stack-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
        <Option icon={<Icon name="crown" />} title={t('role.owner.title')} desc={t('role.owner.desc')} selected={role === 'owner'} onClick={() => setRole('owner')} />
        <Option icon={<Icon name="users" />} title={t('role.chatter.title')} desc={t('role.chatter.desc')} selected={role === 'chatter'} onClick={() => setRole('chatter')} />
      </div>
      <div style={{ marginTop: 26 }}>
        <Btn wide disabled={!role} style={{ opacity: role ? 1 : 0.5 }} onClick={() => role && go(role)} icon="arrow">{t('role.continue')}</Btn>
      </div>
    </Panel>
  );
  else if (step === 'owner') content = (
    <Panel anim={animKey} width={540}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title k={t('owner.kicker')} h={t('owner.title')} sub={t('owner.sub')} />
      <div style={{ border: `1.5px dashed ${C.line2}`, borderRadius: 18, padding: '28px 24px', textAlign: 'center', background: 'color-mix(in srgb, var(--om-accent) 5%, transparent)' }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: 'color-mix(in srgb, var(--om-accent) 18%, transparent)', color: C.purpleHi, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Icon name="plus" size={26} /></div>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>{t('owner.addTitle')}</div>
        <div style={{ color: C.muted, fontSize: 13.5, marginTop: 6 }}>{t('owner.addDesc')}</div>
        <div style={{ marginTop: 18 }}><Btn onClick={handleAddAccount}>{t('owner.addCta')}</Btn></div>
      </div>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <a onClick={finish} style={{ color: C.faint, fontSize: 13.5, cursor: 'pointer' }}>{t('owner.later')}</a>
      </div>
    </Panel>
  );
  else if (step === 'chatter') content = (
    <Panel anim={animKey} width={540}>
      <Head n={dotN} i={dotIndex} label={stepLabel} onBack={hist.length ? back : null} />
      <Title k={t('chatter.kicker')} h={t('chatter.title')} sub={t('chatter.sub')} />
      <div style={{ background: 'color-mix(in srgb, var(--om-ink) 4%, transparent)', border: `1px solid ${C.line}`, borderRadius: 16, padding: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ color: C.faint, fontSize: 12, fontWeight: 600, marginBottom: 5 }}>{t('chatter.usernameLabel')}</div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 20 }}>{usernameDisplay}</div>
        </div>
        <Btn kind="soft" onClick={copyUsername}><Icon name={copied ? 'check' : 'copy'} size={17} />{copied ? t('chatter.copied') : t('chatter.copy')}</Btn>
      </div>
      <p style={{ color: C.faint, fontSize: 13, marginTop: 15, lineHeight: 1.5 }}>{t('chatter.note')}</p>
      <div style={{ marginTop: 22 }}><Btn wide onClick={() => go('done')}>{t('chatter.done')}</Btn></div>
    </Panel>
  );
  else if (step === 'done') content = (
    <Panel anim={animKey} width={500}>
      <div style={{ width: 54, height: 54, borderRadius: '50%', background: '#1f8a5b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(0,0,0,.5)', animation: 'om-pop .35s ease' }}><Icon name="check" size={28} /></div>
      <h1 style={{ color: C.text, fontSize: 27, fontWeight: 800, margin: '18px 0 0' }}>{t('done.title')}</h1>
      <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.55, margin: '11px 0 0' }}>{role === 'chatter' ? t('done.bodyChatter') : t('done.bodyOwner')}</p>
      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <Btn onClick={finish} icon="arrow">{t('done.cta')}</Btn>
      </div>
    </Panel>
  );

  return (
    <OnboardingStage pose={POSE_FOR[step]} onClose={handleClose}>
      {content}
    </OnboardingStage>
  );
}
